import { Box, Button, Checkbox, Dialog, DialogContent, DialogProps, DialogTitle, Divider, Stack, Typography } from '@mui/material';
import useFormBuilder from 'hooks/useFormBuilder';
import useFormBuilderTemplate from 'hooks/useFormBuilderTemplate';
import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import DialogClose from 'views/components/buttons/DialogClose';
import useAssignedForms from './useAssignedForms';

interface Props extends DialogProps {
  onClose: () => void;
  userId: string; // patient id
  therapistId: string;
  alreadyAssignedForms: any;
  alreadyAssignedTemplates: any;
}

const AttachFormBasedFormModal = ({ userId, therapistId, onClose, alreadyAssignedForms, alreadyAssignedTemplates, ...props }: Props) => {
  const { forms, refetchForms } = useFormBuilder({ userId });
  const { templates, refetchUserTemplates } = useFormBuilderTemplate({ userId });
  useEffect(() => {
    refetchForms();
    refetchUserTemplates();
    console.log('ran');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const { createAssignmentAsync, createAssignmentIsLoading } = useAssignedForms({ patientId: userId, therapistId });
  const unassignedForms = useMemo(
    () =>
      forms?.filter(
        (userForm) => alreadyAssignedForms?.indexOf(userForm._id) === -1 && userForm?.status === true && userForm?.archive !== true
      ),
    [forms, alreadyAssignedForms]
  );
  const unassignedTemplates = useMemo(
    () =>
      templates?.filter(({ _id, status, archive }) => alreadyAssignedTemplates?.indexOf(_id) === -1 && status === true && archive !== true),
    [templates, alreadyAssignedTemplates]
  );
  const [formsMarkedForAssignment, setFormsMarkedForAssignment] = useState<any>([]);
  const [formsTemplatesForAssignment, setTemplatesMarkedForAssignment] = useState<any>([]);
  const handleMarkFormForAssignment = (e: any) => {
    e.target.checked
      ? setFormsMarkedForAssignment((prev) => [...prev, e.target.id])
      : setFormsMarkedForAssignment((prev) => prev?.filter((formId) => formId !== e.target.id));
  };
  const handleMarkTemplateForAssignment = (e: any) => {
    e.target.checked
      ? setTemplatesMarkedForAssignment((prev) => [...prev, e.target.id])
      : setTemplatesMarkedForAssignment((prev) => prev.filter((templateId) => templateId !== e.target.id));
  };
  const handleAssignment = async () => {
    await createAssignmentAsync({
      forms: formsMarkedForAssignment,
      templates: formsTemplatesForAssignment
    });
    onClose();
  };

  return (
    <Dialog maxWidth="sm" fullWidth sx={{ '& .MuiDialog-paper': { p: 0 } }} onClose={onClose} {...props}>
      <DialogTitle>
        <FormattedMessage defaultMessage="Please select form(s) or template(s) to assign to client" />
        <DialogClose onClose={onClose} />
      </DialogTitle>
      <DialogContent>
        {unassignedTemplates?.length ? (
          <Typography variant="h5">
            <FormattedMessage defaultMessage="Mark Templates for assignment" />
          </Typography>
        ) : (
          <Typography variant="body2">
            <FormattedMessage defaultMessage="No Available Templates for assignment" />
          </Typography>
        )}
        <Box sx={{ maxHeight: '20vh', overflowY: 'scroll' }}>
          {unassignedTemplates?.map((unassignedTemplate) => (
            <Stack direction="row" gap={1} key={unassignedTemplate._id} alignItems="center">
              <Checkbox
                id={unassignedTemplate._id}
                checked={formsTemplatesForAssignment.includes(unassignedTemplate._id)}
                onChange={handleMarkTemplateForAssignment}
              ></Checkbox>
              <Typography>{unassignedTemplate?.name}</Typography>
            </Stack>
          ))}
        </Box>
        <Divider sx={{ mt: 1 }} />
        {unassignedForms?.length ? (
          <Typography variant="h5">
            <FormattedMessage defaultMessage="Mark Forms for assignment" />
          </Typography>
        ) : (
          <Typography variant="body2">
            <FormattedMessage defaultMessage="No Available Forms for assignment" />
          </Typography>
        )}
        <Box sx={{ maxHeight: '20vh', overflowY: 'scroll' }}>
          {unassignedForms
            ?.filter((form) => !form.isForClinicDocuments)
            .map((unassignedForm) => (
              <Stack direction="row" gap={1} key={unassignedForm._id} alignItems="center">
                <Checkbox
                  id={unassignedForm._id}
                  checked={formsMarkedForAssignment?.includes(unassignedForm._id)}
                  onChange={handleMarkFormForAssignment}
                ></Checkbox>
                <Typography>{unassignedForm?.name}</Typography>
              </Stack>
            ))}
        </Box>
        <Divider />
        <Button variant="contained" sx={{ mt: 1 }} onClick={handleAssignment} disabled={createAssignmentIsLoading}>
          <FormattedMessage defaultMessage="Assign Checked Forms and Templates to Client" />
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default AttachFormBasedFormModal;
